import { useState, useMemo } from "react";
import { Timer } from "@phosphor-icons/react";
import { ToolShell } from "@/components/ToolShell";
import { Slider } from "@/components/ui/slider";
import { useTrackOnce } from "@/hooks/useTrackOnce";
import { useSeo } from "@/lib/seo";

const fmtDur = (mins) => {
  const total = Math.round(mins * 60);
  const m = Math.floor(total / 60), s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function ReadingTimer() {
  useSeo("Script Reading Timer", "Paste your video script and get an estimated on-camera duration based on real speaking speeds.");
  const [script, setScript] = useState("");
  const [wpm, setWpm] = useState(140);
  const track = useTrackOnce("reading_timer");

  const words = useMemo(() => script.trim().split(/\s+/).filter(Boolean).length, [script]);

  const onChange = (v) => {
    setScript(v);
    if (v.trim().length > 20) track();
  };

  return (
    <ToolShell slug="reading-timer" title="Script Reading Timer" tagline="Paste your script, pick your pace, and know your video length before you hit record." hasResults={words > 0}>
      <div className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold">Your script</label>
          <span className="text-sm font-bold text-[var(--glow)]" data-testid="word-count">{words.toLocaleString()} words</span>
        </div>
        <textarea
          data-testid="script-input"
          value={script}
          onChange={(e) => onChange(e.target.value)}
          rows={8}
          placeholder="Paste your full video script here..."
          className="w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)] resize-y"
        />
        <div className="mt-6">
          <div className="flex justify-between mb-3">
            <label className="text-sm font-semibold">Speaking pace</label>
            <span className="text-sm font-bold text-[var(--glow)]" data-testid="wpm-value">{wpm} wpm</span>
          </div>
          <Slider data-testid="wpm-slider" value={[wpm]} onValueChange={(v) => setWpm(v[0])} min={100} max={200} step={5} />
          <div className="flex justify-between text-[10px] text-[var(--muted)] mt-1.5">
            <span>100 — slow & clear</span><span>140 — typical YouTuber</span><span>200 — rapid-fire</span>
          </div>
        </div>
      </div>

      {words > 0 && (
        <div className="mt-8 grid grid-cols-3 gap-3 fade-up" data-testid="duration-results">
          {[
            { label: "Slow (130 wpm)", mins: words / 130, testId: "duration-slow" },
            { label: `Your pace (${wpm} wpm)`, mins: words / wpm, testId: "duration-custom", hot: true },
            { label: "Fast (150 wpm)", mins: words / 150, testId: "duration-fast" },
          ].map((d) => (
            <div key={d.label} className={`surface rounded-[20px] p-5 text-center ${d.hot ? "border-[var(--glow)]" : ""}`}>
              <Timer size={18} color={d.hot ? "var(--glow)" : "var(--muted)"} weight="duotone" className="mx-auto" />
              <p className="font-display font-black text-xl sm:text-3xl mt-2" style={{ color: d.hot ? "var(--glow)" : "var(--text)" }} data-testid={d.testId}>{fmtDur(d.mins)}</p>
              <p className="text-[10px] text-[var(--muted)] mt-1">{d.label}</p>
            </div>
          ))}
        </div>
      )}
    </ToolShell>
  );
}
